import React, { useState, useEffect } from 'react';

/**
 * BorsamexCircuit - Crypto Signal Pipeline
 * 
 * Real-time market data to signal generation flow.
 * Shows: FEED → NORM → SIGNAL → RISK → EXEC
 */

export function BorsamexCircuit({ className = '' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`relative ${className} transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ width: '260px', height: '180px' }}
    >
      <svg 
        viewBox="0 0 130 90" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="borsamexGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <pattern id="borsamexGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(15, 118, 110, 0.08)" strokeWidth="0.3"/>
          </pattern>
        </defs>

        {/* Background grid */}
        <rect width="130" height="90" fill="url(#borsamexGrid)" />

        {/* Connection paths */}
        <g className="connections">
          {/* WebSocket feeds merging */}
          <path d="M 12 25 L 22 25 L 30 40" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          <path d="M 12 45 L 30 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 12 65 L 22 65 L 30 50" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          
          {/* Normalizer to Signal Engine */}
          <path d="M 45 45 L 52 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Signal to Risk */}
          <path d="M 72 45 L 82 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Risk to Exec */}
          <path d="M 97 45 L 107 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Model feedback loop (dashed) */}
          <path d="M 62 55 L 62 72 L 38 72 L 38 52" fill="none" stroke="rgba(15, 118, 110, 0.25)" strokeWidth="0.4" strokeDasharray="2,2" />
          
          {/* Position state feedback */}
          <path d="M 89 38 L 89 22 L 62 22 L 62 38" fill="none" stroke="rgba(15, 118, 110, 0.2)" strokeWidth="0.4" strokeDasharray="1.5,1.5" />
        </g>

        {/* Data pulse */}
        <g className="data-pulse">
          <circle r="1.5" fill="#0F766E" filter="url(#borsamexGlow)">
            <animateMotion dur="6s" repeatCount="indefinite">
              <mpath href="#borsamexPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;1;0" dur="6s" repeatCount="indefinite" />
          </circle>
          <path id="borsamexPath" d="M 12 45 L 37 45 L 62 45 L 89 45 L 118 45" fill="none" stroke="none" />
        </g>

        {/* Nodes with labels */}
        <g className="nodes" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* Data Feeds */}
          <rect x="5" y="21" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="27" textAnchor="middle" fill="#0F766E" fontSize="3.5">BTC</text>
          
          <rect x="5" y="41" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="11" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">ETH</text>
          
          <rect x="5" y="61" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="67" textAnchor="middle" fill="#0F766E" fontSize="3.5">ALT</text>
          
          {/* Normalizer */}
          <rect x="30" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="38" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">NORM</text>
          <text x="38" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">ws</text>
          
          {/* Signal Engine (Core) */}
          <rect x="52" y="36" width="20" height="18" rx="2" fill="rgba(15, 118, 110, 0.15)" stroke="#0F766E" strokeWidth="1" />
          <text x="62" y="44" textAnchor="middle" fill="#0F766E" fontSize="5" fontWeight="600">SIGNAL</text>
          <text x="62" y="50" textAnchor="middle" fill="rgba(15, 118, 110, 0.8)" fontSize="3">ml</text>
          
          {/* Risk Module */}
          <rect x="82" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="90" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">RISK</text>
          <text x="90" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">check</text>
          
          {/* Execution */}
          <rect x="107" y="40" width="14" height="10" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.45)" strokeWidth="0.6" />
          <text x="114" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5">EXEC</text>
        </g>

        {/* Technical annotations */}
        <g className="annotations" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <text x="62" y="62" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">p99 &lt;25ms</text>
          <text x="50" y="76" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">retrain</text>
          <text x="75" y="19" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">state</text>
        </g>

        {/* Status indicator */}
        <g className="status">
          <circle cx="6" cy="6" r="2.5" fill="#10B981">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="8" fill="#10B981" fontSize="3.5" fontWeight="600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>LIVE</text>
        </g>
      </svg>
    </div>
  );
}

export default BorsamexCircuit;
